import { useState, useRef, useEffect, useCallback } from 'react';
import { buildApiUrl } from '../../config/env';
// Removed unused auth import

export const useTextToSpeech = () => {
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [speakingMessageId, setSpeakingMessageId] = useState(null);
  const [audioSource, setAudioSource] = useState(null);
  
  const mediaSourceRef = useRef(null);
  const sourceBufferRef = useRef(null);
  const audioRef = useRef(null);

  const appendBuffer = (chunk) => {
    return new Promise((resolve, reject) => {
      const sourceBuffer = sourceBufferRef.current;
      if (!sourceBuffer) return resolve();

      const handleUpdateEnd = () => {
        sourceBuffer.removeEventListener("error", handleError);
        resolve();
      };

      const handleError = (e) => {
        sourceBuffer.removeEventListener("updateend", handleUpdateEnd);
        reject(e);
      };

      sourceBuffer.addEventListener("updateend", handleUpdateEnd, { once: true });
      sourceBuffer.addEventListener("error", handleError, { once: true });

      try {
        sourceBuffer.appendBuffer(chunk);
      } catch (err) {
        reject(err);
      }
    });
  };

  const stopStreaming = useCallback(() => {
    if (mediaSourceRef.current && mediaSourceRef.current.readyState === "open") {
      mediaSourceRef.current.endOfStream();
    }
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = "";
    }
    if (audioSource) {
      URL.revokeObjectURL(audioSource);
      setAudioSource(null);
    }
    setIsSpeaking(false);
    setSpeakingMessageId(null);
  }, [audioSource]);

  const handleSpeak = async (text, messageId) => {
    // If already speaking the same message, STOP
    if (isSpeaking && speakingMessageId === messageId) {
      stopStreaming();
      return;
    }
    // Otherwise, stop any current stream and start a new one
    stopStreaming();
    setIsSpeaking(true);
    setSpeakingMessageId(messageId);

    const mediaSource = new MediaSource();
    mediaSourceRef.current = mediaSource;

    mediaSource.addEventListener("sourceopen", async () => {
      try {
        // Add a source buffer for MPEG audio
        sourceBufferRef.current = mediaSource.addSourceBuffer("audio/mpeg");

        // Fetch the TTS stream from your backend
        const url = buildApiUrl(`/tts?text=${encodeURIComponent(text)}`);
        const response = await fetch(url);
        if (!response.body) {
          throw new Error("No response body from TTS stream");
        }

        const reader = response.body.getReader();
        let done = false;
        while (!done) {
          const { value, done: readerDone } = await reader.read();
          if (readerDone) {
            done = true;
            if (mediaSource.readyState === "open") {
              mediaSource.endOfStream();
            }
            break;
          }
          // Wait for the sourceBuffer to finish updating before appending the next chunk
          await appendBuffer(value);
        }
      } catch (err) {
        console.error("Error reading TTS stream:", err);
        if (mediaSource.readyState === "open") {
          mediaSource.endOfStream();
        }
      }
    });

    // Create an object URL for the MediaSource and set it as the audio source
    const objectURL = URL.createObjectURL(mediaSource);
    setAudioSource(objectURL);
  };

  useEffect(() => {
    // Clean up on unmount
    return () => {
      stopStreaming();
    };
  }, [stopStreaming]);

  return {
    isSpeaking,
    speakingMessageId,
    audioSource,
    audioRef,
    handleSpeak,
    stopStreaming
  };
};
